// src/api/engieApi.ts — client du connecteur engie (ts-ingest, process indépendant de report-api,
// cf. CLAUDE.md section "Connecteur engie"). Appelle directement /ts/engie/... (jamais via /api).
// Mirror de src/api/deepkiApi.ts, adapté au modèle "un fichier, N compteurs" (ÉLEC et/ou GAZ).

import { APP_CONFIG } from "../config/app.config";

const BASE = APP_CONFIG.tsIngestBaseUrl;

async function tsRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: init?.body != null ? { "Content-Type": "application/json" } : undefined,
  });
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`ts-ingest ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export type EngieEnergyType = "elec" | "gaz";

export interface EngieMeterCandidate {
  key:            string;
  label:          string;
  energyType:     EngieEnergyType;
  unit:           string;
  monthlyByYear:  Record<string, (number | null)[]>;
  indexByYear?:   Record<string, (number | null)[]>;
  yearsPresent:   number[];
}

export interface EngieParseResult {
  years:         number[];
  meters:        EngieMeterCandidate[];
  ignoredSheets: string[];
  warnings:      string[];
}

export interface EngieMappingEntry {
  key:        string;
  meterId:    string;
  energyType: EngieEnergyType;
}

export interface EngieIngestResult {
  ok:             boolean;
  batchId:        string;
  storedFilename: string;
  results:        Array<{ meterId: string; energyType: EngieEnergyType; rowsIngested: number }>;
}

export interface EngieImportRecord {
  id:               string;
  batchId:          string;
  siteId:           string;
  meterId:          string;
  energyType:       string;
  originalFilename: string;
  storedFilename:   string;
  labelInFile:      string;
  yearsCovered:     string;
  rowCount:         number;
  createdAt:        string;
  deletedAt:        string | null;
}

export function validateEngieFile(input: { filename: string; dataBase64: string }): Promise<EngieParseResult> {
  return tsRequest<EngieParseResult>("/engie/validate", { method: "POST", body: JSON.stringify(input) });
}

export function ingestEngieFile(input: {
  siteId: string; filename: string; dataBase64: string; mapping: EngieMappingEntry[];
}): Promise<EngieIngestResult> {
  return tsRequest<EngieIngestResult>("/engie/ingest", { method: "POST", body: JSON.stringify(input) });
}

export function getEngieReadings(meterId: string, years?: number[]): Promise<{ data: [number, number][] }> {
  const qs = years?.length ? `?years=${years.join("_")}` : "";
  return tsRequest<{ data: [number, number][] }>(`/engie/readings/${encodeURIComponent(meterId)}${qs}`);
}

/** Gaz uniquement — forme `Releve[]` (t en ms, compatible IndexTable de MeterConsoModal.tsx). */
export function getEngieIndexReadings(meterId: string, years?: number[]): Promise<{ data: { t: number; i1: number }[] }> {
  const qs = years?.length ? `?years=${years.join("_")}` : "";
  return tsRequest<{ data: { t: number; i1: number }[] }>(`/engie/index/${encodeURIComponent(meterId)}${qs}`);
}

export function getEngieLastImport(meterId: string): Promise<EngieImportRecord | null> {
  return tsRequest<EngieImportRecord | null>(`/engie/imports/${encodeURIComponent(meterId)}`);
}

export function getEngieCounts(meterId: string): Promise<{ monthsCount: number }> {
  return tsRequest<{ monthsCount: number }>(`/engie/counts/${encodeURIComponent(meterId)}`);
}

/** Purge par compteur (pas par batch) — réservé aux admins côté UI. */
export function purgeEngieMeter(meterId: string): Promise<{ ok: boolean; readingsDeleted: number; importsMarkedDeleted: number }> {
  return tsRequest(`/engie/meter/${encodeURIComponent(meterId)}`, { method: "DELETE" });
}
